import { Dispatch } from 'react-redux';
import { Action } from '../../actions';
import { ListDisplayType } from './constants';
import { Api } from '../../typings/api';
import { StoreState } from '../../reducers';
import {
	getDriveItemStatusFromMap,
	DriveItemFetchState,
	DriveListState,
	FetchStatus,
	FolderState,
	DriveItemInfo
} from './reducer';
import { requestMobileAppDownload, deleteItem } from '../DownloadManager/actions';
import { syncOfflineDependenciesAfterContentRefresh } from '../DownloadManager/actions/download';
import { DownloadMode } from '../DownloadManager/reducer';
import { RouterParamsState } from '../SetRouteParams/reducer';
import NativeApi, { OpenFrameReturn } from '../../utils/nativeApi';
import UrlUtils from '../../utils/url';
import UserAgentUtils from '../../utils/userAgent';
import DriveItemUtils from '../../utils/driveItem';
import { getGallerySessionId, shouldIgnoreGallerySessionWork } from '../../utils/sessionCancellation';
import {
	logOfflineDownload,
	OfflineDownloadDebugAction,
	OfflineDownloadPhase,
	OfflineDownloadTimedPhase,
	startTimedPhase,
	endTimedPhase,
} from '../../utils/offlineDownloadDebug';
import Utils, { downloadsFolder, openedFilesFolder, tempFolder } from '../../utils';
import ApiUtils from '../../utils/api';
import { DriveItemListOrigin } from '../DriveItemListPage/constants';
import { createNotification } from '../NotificationBar/actions';
import RoutesUtils from '../../utils/routes';
import DownloadManagerUtils from '../../utils/downloadManager';
import StatsUtils from '../../utils/statistics';
import { updateLastVersionRead } from '../ReadStatus/actions';
import store from '../../store';
import {
	downloadThumb,
	startContentStructurePreparation,
	endContentStructurePreparation,
} from '../OfflineManager/actions';
import { DownloadPriority } from '../../utils/nativeApi';
import RouteUtils from '../../utils/routes';
import { removeFromSelection } from '../Selection/actions';
import { setLastPublication } from '../BackToPublication/actions';
import { getProfileData } from '../ProfilePage/actions';
import { checkActiveSession } from '../MultiAppAuthentication/actions';
import { share } from '../Share/actions';
import IndexedDBManager from '../../utils/indexedDB/manager';
import { DownloadStatus } from '../OfflineManager/reducer';

enum DriveItemListActionKeys {
	REQUEST_DRIVE_META = '@@driveList/REQUEST_DRIVE_META',
	RECEIVE_DRIVE_META = '@@driveList/RECEIVE_DRIVE_META',
	FETCH_ERROR_DRIVE_META = '@@driveList/FETCH_ERROR_DRIVE_META',
	REQUEST_CONTENT_BLOCK = '@@driveList/REQUEST_CONTENT_BLOCK',
	RECEIVE_CONTENT_BLOCK = '@@driveList/RECEIVE_CONTENT_BLOCK',
	FETCH_ERROR_CONTENT_BLOCK = '@@driveList/FETCH_ERROR_CONTENT_BLOCK',
	RECEIVE_DRIVE_ITEMS = '@@driveList/RECEIVE_DRIVE_ITEMS',
	REMOVE_DRIVE_ITEMS = '@@driveList/REMOVE_DRIVE_ITEMS',
	RECEIVE_RESOLUTIONS = '@@driveList/RECEIVE_RESOLUTIONS',
	UPDATE_LIST_DISPLAY_TYPE = '@@driveList/UPDATE_LIST_DISPLAY_TYPE',
	UPDATE_DRIVE_ITEM = '@@driveList/UPDATE_DRIVE_ITEM',
	RESET_FETCH_ALL_STATUS = '@@driveList/RESET_FETCH_ALL_STATUS',
	REQUEST_ALL = '@@driveList/REQUEST_ALL',
	RECEIVE_ALL = '@@driveList/RECEIVE_ALL',
	OPEN_CONTEXTUAL_MENU = '@@driveList/OPEN_CONTEXTUAL_MENU',
	CLOSE_CONTEXTUAL_MENU = '@@driveList/CLOSE_CONTEXTUAL_MENU',
	LOCK_CONTEXTUAL_MENU = '@@driveList/LOCK_CONTEXTUAL_MENU',
	UNLOCK_CONTEXTUAL_MENU = '@@driveList/UNLOCK_CONTEXTUAL_MENU',
	SET_BREADCRUMBS = '@@driveList/SET_BREADCRUMBS',
	RESET_BREADCRUMBS = '@@driveList/RESET_BREADCRUMBS',
	TOGGLE_EDIT_MODE = '@@driveList/TOGGLE_EDIT_MODE',
	MOVE_DRIVE_ITEM = '@@driveList/MOVE_DRIVE_ITEM',
	MOVE_DRIVE_ITEM_IN_FOLDER = '@@driveList/MOVE_DRIVE_ITEM_IN_FOLDER',
	RESET_DRIVE_ORDER = '@@driveList/RESET_DRIVE_ORDER',
	SET_LOADING_STATUS = '@@driveList/SET_LOADING_STATUS',
	SET_FOLDER_DISPLAY_TYPE = '@@driveList/SET_FOLDER_DISPLAY_TYPE',
	SET_FOLDERS_DISPLAY_TYPE = '@@driveList/SET_FOLDERS_DISPLAY_TYPE',
	RESET_FOLDERS_DISPLAY_TYPE = '@@driveList/RESET_FOLDERS_DISPLAY_TYPE',
	SET_CUSTOM_TEMPLATES_IDS = '@@driveList/SET_CUSTOM_TEMPLATES_IDS',
	ADD_CUSTOM_TEMPLATE = '@@driveList/ADD_CUSTOM_TEMPLATE',
	REMOVE_CUSTOM_TEMPLATE = '@@driveList/REMOVE_CUSTOM_TEMPLATE',
	START_FOLDER_TREE_UPDATE = '@@driveList/START_FOLDER_TREE_UPDATE',
	END_FOLDER_TREE_UPDATE = '@@driveList/END_FOLDER_TREE_UPDATE',
	SET_LAST_FOLDER_TREE_UPDATE = '@@driveList/SET_LAST_FOLDER_TREE_UPDATE',
}

interface RequestDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.REQUEST_DRIVE_META;
	driveItemId: number;
}

interface ReceiveDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_DRIVE_META;
	driveItemId: number;
}

interface FetchErrorDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.FETCH_ERROR_DRIVE_META;
	driveItemId: number;
}

interface RequestContentBlockAction extends Action {
	type: DriveItemListActionKeys.REQUEST_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveContentBlockAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveDriveItemsAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_DRIVE_ITEMS;
	driveItems: { [k: number]: Api.DriveItem };
	isInGallery?: boolean;
}

interface RemoveDriveItemsAction extends Action {
	type: DriveItemListActionKeys.REMOVE_DRIVE_ITEMS;
	driveItemIds: number[];
}

interface FetchErrorContentBlockAction extends Action {
	type: DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveResolutionsAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_RESOLUTIONS;
	resolutions: { [k: string]: Api.ResolutionGroup };
}

interface UpdateListDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.UPDATE_LIST_DISPLAY_TYPE;
	displayType: ListDisplayType;
}

interface UpdateDriveItemAction extends Action {
	type: DriveItemListActionKeys.UPDATE_DRIVE_ITEM;
	item: Api.DriveItem;
}

interface ResetFetchAllStatusAction extends Action {
	type: DriveItemListActionKeys.RESET_FETCH_ALL_STATUS;
}

interface RequestAllDriveItems extends Action {
	type: DriveItemListActionKeys.REQUEST_ALL;
}

interface ReceiveAllDriveItems extends Action {
	type: DriveItemListActionKeys.RECEIVE_ALL;
}

interface OpenContextualMenuAction extends Action {
	type: DriveItemListActionKeys.OPEN_CONTEXTUAL_MENU;
	driveItemId: number;
}

interface CloseContextualMenuAction extends Action {
	type: DriveItemListActionKeys.CLOSE_CONTEXTUAL_MENU;
}

interface LockContextualMenuAction extends Action {
	type: DriveItemListActionKeys.LOCK_CONTEXTUAL_MENU;
}

interface UnlockContextualMenuAction extends Action {
	type: DriveItemListActionKeys.UNLOCK_CONTEXTUAL_MENU;
}

interface SetBreadcrumbsAction extends Action {
	type: DriveItemListActionKeys.SET_BREADCRUMBS;
	item: FolderState;
}

interface ResetBreadcrumbsAction extends Action {
	type: DriveItemListActionKeys.RESET_BREADCRUMBS;
}

interface ToggleEditModeAction extends Action {
	type: DriveItemListActionKeys.TOGGLE_EDIT_MODE;
}

interface MoveDriveItemAction extends Action {
	type: DriveItemListActionKeys.MOVE_DRIVE_ITEM;
	folderId: number;
	from: number;
	to: number;
	filteredResources: Api.DriveItemResource[];
	isAppRootFolder: boolean;
	privateDirId: number | undefined;
}

interface MoveDriveItemInFolderAction extends Action {
	type: DriveItemListActionKeys.MOVE_DRIVE_ITEM_IN_FOLDER;
	folderId: number;
	id: number;
}

interface ResetDriveOrderAction extends Action {
	type: DriveItemListActionKeys.RESET_DRIVE_ORDER;
	folderId: number;
	resources: Api.DriveItemResource[];
}

interface SetLoadingStatusAction extends Action {
	type: DriveItemListActionKeys.SET_LOADING_STATUS;
	isLoading: boolean;
}

interface SetFolderDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.SET_FOLDER_DISPLAY_TYPE;
	folderId: number | string;
	displayType: ListDisplayType;
}

interface SetFoldersDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.SET_FOLDERS_DISPLAY_TYPE;
	data: { [k: string]: ListDisplayType };
}

interface ResetFoldersDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.RESET_FOLDERS_DISPLAY_TYPE;
}

interface SetCustomTemplatesIdsAction extends Action {
	type: DriveItemListActionKeys.SET_CUSTOM_TEMPLATES_IDS;
	ids: number[];
}

interface AddCustomTemplateAction extends Action {
	type: DriveItemListActionKeys.ADD_CUSTOM_TEMPLATE;
	id: number;
}

interface RemoveCustomTemplateAction extends Action {
	type: DriveItemListActionKeys.REMOVE_CUSTOM_TEMPLATE;
	id: number;
}

interface StartFolderTreeUpdateAction extends Action {
	type: DriveItemListActionKeys.START_FOLDER_TREE_UPDATE;
}

interface EndFolderTreeUpdateAction extends Action {
	type: DriveItemListActionKeys.END_FOLDER_TREE_UPDATE;
}

interface SetLastFolderTreeUpdateAction extends Action {
	type: DriveItemListActionKeys.SET_LAST_FOLDER_TREE_UPDATE;
	lastFolderTreeUpdate: number;
}

type DriveItemListAction =
	| RequestDriveItemMetaAction
	| ReceiveDriveItemMetaAction
	| FetchErrorDriveItemMetaAction
	| RequestContentBlockAction
	| ReceiveContentBlockAction
	| FetchErrorContentBlockAction
	| ReceiveDriveItemsAction
	| ReceiveResolutionsAction
	| RemoveDriveItemsAction
	| UpdateListDisplayTypeAction
	| UpdateDriveItemAction
	| ResetFetchAllStatusAction
	| RequestAllDriveItems
	| ReceiveAllDriveItems
	| OpenContextualMenuAction
	| CloseContextualMenuAction
	| LockContextualMenuAction
	| UnlockContextualMenuAction
	| SetBreadcrumbsAction
	| ResetBreadcrumbsAction
	| ToggleEditModeAction
	| MoveDriveItemAction
	| MoveDriveItemInFolderAction
	| ResetDriveOrderAction
	| SetLoadingStatusAction
	| SetFolderDisplayTypeAction
	| SetFoldersDisplayTypeAction
	| ResetFoldersDisplayTypeAction
	| SetCustomTemplatesIdsAction
	| AddCustomTemplateAction
	| RemoveCustomTemplateAction
	| StartFolderTreeUpdateAction
	| EndFolderTreeUpdateAction
	| SetLastFolderTreeUpdateAction;

enum DriveItemOrigin {
	List = 'list',
	Details = 'details',
	Menu = 'menu'
}


export type CallbackAction = ActionNone | ActionViewDriveItem | ActionSAMLLogin | ActionShare;

export enum ActionType {
	NONE = 'none',
	VIEW_DRIVE_ITEM = 'viewDriveItem',
	SAML_LOGIN = 'SAMLLogin',
	SHARE = 'share',
}

interface ActionNone {
	type: ActionType.NONE;
}

interface ActionViewDriveItem {
	type: ActionType.VIEW_DRIVE_ITEM;
	assetId: number;
	page?: number;
}

interface ActionSAMLLogin {
	type: ActionType.SAML_LOGIN;
	token: string;
}

interface ActionShare {
	type: ActionType.SHARE;
}

const fetchDriveItem = (driveItemId: number, driveFolderId?: number, forceFetch?: boolean) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{} | Api.DriveItem> => {
		const { driveList } = getState();

		if (!driveList.driveItems[driveItemId]) {
			if (driveFolderId && driveList.driveItems[driveFolderId]) {
				const driveFolder = driveList.driveItems[driveFolderId];
				const resource = driveFolder.resources ? driveFolder.resources.filter(resource => resource.id === driveItemId)[0] : undefined;

				if (resource) {
					return dispatch(fetchContentBlock(driveFolderId, resource.block, forceFetch));
				}
			}
			return dispatch(getOrFetchDriveItemMeta(driveItemId));
		}
		return Promise.resolve(driveList.driveItems[driveItemId]);
	};

const resetFetchAllStatus = (): ResetFetchAllStatusAction => ({
	type: DriveItemListActionKeys.RESET_FETCH_ALL_STATUS,
});

let getAllDriveItemsPromise: Promise<number[]>;

const getAllDriveItems = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<number[]> => {
	const { driveList, galleryInfos } = getState();

	if (driveList.fetchAllFetchStatus === FetchStatus.NOT_FETCHED) {
		dispatch({ type: DriveItemListActionKeys.REQUEST_ALL });

		const { applicationGallery } = galleryInfos.config;
		const initChildrenPromise = dispatch(initChildren());

		getAllDriveItemsPromise = initChildrenPromise
			.then(() => {
				dispatch({ type: DriveItemListActionKeys.RECEIVE_ALL });
				dispatch(updateLastFolderTreeUpdate());
				dispatch(endFolderTreeUpdate());

				const { driveList: { driveItems } } = getState();
				return Object.keys(driveItems)
					.map(id => parseInt(id, 10))
					.filter(id => id !== applicationGallery);
			});
	}
	return getAllDriveItemsPromise;
};

const initChildren = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const {
		appInfos,
		galleryInfos: { config: { clientId } },
	} = getState();
	const isMultiApp = Utils.isMultiApp(appInfos);

	if (isMultiApp) {
		return dispatch(getProfileData(clientId))
			.then(() => {
				return ApiUtils.getAppMeta()
					.then((appMeta: Api.NestedAppMeta) => {
						if (appMeta) {
							return dispatch(receiveNestedDriveItems(appMeta.app))
								.then(() => appMeta.sharedDir && dispatch(receiveNestedDriveItems(appMeta.sharedDir)));
						}
						return {};
					});
			});
	}

	return ApiUtils.getChildrenMeta()
		.then((childrenMeta: Api.NestedDriveItems) => {
			if (childrenMeta) {
				return dispatch(receiveNestedDriveItems(childrenMeta));
			}
			return {};
		});
};

const receiveNestedDriveItems = (nestedDriveItems: Api.NestedDriveItems) =>
	(dispatch: Dispatch<StoreState>): Promise<{}> => {

		return dispatch(receiveDriveItemFullMeta(nestedDriveItems))
			.then(() => {
				const { resources = [] } = nestedDriveItems;

				// Process resources in batches to avoid blocking the UI thread
				// Smaller batch size (5) ensures UI remains responsive even with deeply nested structures
				// This allows user interactions to be processed between batches
				return processResourcesInBatches(resources, dispatch, 5);
			});
	};

// Process resources in batches with yields to the event loop to keep UI responsive
// Smaller batches (5 items) ensure UI remains responsive even with deeply nested structures
// We also yield after processing each item within a batch to maximize responsiveness
const processResourcesInBatches = (
	resources: Api.NestedDriveItems[],
	dispatch: Dispatch<StoreState>,
	batchSize: number = 5
): Promise<{}> => {
	if (resources.length === 0) {
		return Promise.resolve({});
	}

	// Process first batch with yields between items
	const batch = resources.slice(0, batchSize);
	const remaining = resources.slice(batchSize);

	// Process batch items sequentially with yields to maximize UI responsiveness
	const processBatchSequentially = (items: Api.NestedDriveItems[], index: number = 0): Promise<{}> => {
		if (index >= items.length) {
			return Promise.resolve({});
		}

		return dispatch(receiveNestedDriveItems(items[index]))
			.then(() => {
				// Yield to event loop after each item to allow UI interactions
				return new Promise<{}>((resolve) => {
					setTimeout(() => {
						resolve(processBatchSequentially(items, index + 1));
					}, 0);
				});
			});
	};

	if (remaining.length === 0) {
		// Last batch, process sequentially with yields
		return processBatchSequentially(batch);
	}

	// Process current batch sequentially, then yield before processing next batch
	return processBatchSequentially(batch)
		.then(() => {
			// Additional yield before processing next batch
			return new Promise<{}>((resolve) => {
				setTimeout(() => {
					resolve(processResourcesInBatches(remaining, dispatch, batchSize));
				}, 0);
			});
		});
};

const receiveDriveItemFullMeta = (nestedDriveItems: Api.NestedDriveItems) =>
	(dispatch: Dispatch<StoreState>): Promise<void> => {
		// clone nestedDriveItems but removes resolutions
		let { resolutions, ...nestedDriveItemsWithoutResolutions } = nestedDriveItems;
		let resources: Api.DriveItemResource[] | undefined;
		if (nestedDriveItems.resources) {
			const { resources: nestedDriveItemsResources } = nestedDriveItems;
			resources = nestedDriveItemsResources.map(({ id, type, position, format }) => ({
				id,
				type,
				position,
				format,
			}));
		}

		const driveItem: Api.DriveItem | Api.DriveFolderInfo = {
			...nestedDriveItemsWithoutResolutions,
			resources
		};

		dispatch(receiveDriveItems({
			[driveItem.id]: driveItem
		}));
		if (resolutions) {
			dispatch(receiveResolutions(resolutions));
		}

		return Promise.resolve();
	};

const canMetaBeFetched = (metaFetchStatus: FetchStatus, forceFetch: boolean): boolean => {
	return metaFetchStatus === FetchStatus.NOT_FETCHED
		|| forceFetch && metaFetchStatus === FetchStatus.FETCHED;
};

const getOrFetchDriveItemMeta = (driveItemId: number, isInGallery: boolean = true, isAsync: boolean = true, forceFetch: boolean = false) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{} | Api.DriveItem> => {
		const gallerySessionId = getGallerySessionId();

		if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
			const { driveList } = getState();
			return Promise.resolve(driveList.driveItems[driveItemId] || {});
		}

		const { driveList, galleryInfos } = getState();
		const { applicationGallery: galleryId } = galleryInfos.config;
		const { metaFetchStatus } = getDriveItemStatusFromMap(driveList.driveItemsFetchStatus, driveItemId);
		const driveItemMeta = driveList.driveItems[driveItemId];
		const isItemFetched = !forceFetch && driveItemMeta && metaFetchStatus === FetchStatus.FETCHED;

		if (isItemFetched) {
			return Promise.resolve(driveItemMeta);
		}

		if (canMetaBeFetched(metaFetchStatus, forceFetch) && UserAgentUtils.isOnline()) {
			dispatch(requestDriveItemMeta(driveItemId));

			return ApiUtils.getMeta(driveItemId, isAsync)
				.then(driveItem => {
					if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
						return driveList.driveItems[driveItemId] || {};
					}

					if (driveItem.resolutions) {
						dispatch(receiveResolutions(driveItem.resolutions));
					}

					dispatch(receiveDriveItems({ [driveItem.id]: driveItem }, isInGallery)).then();
					dispatch(receiveDriveItemMeta(driveItemId));

					if (driveItem.id === galleryId) {
						dispatch(setAppDisplayType()).then();
					}

					return dispatch(fetchItemIconMeta(driveItem))
						.then(() => driveItem);
				})
				.catch(error => {
					if (shouldIgnoreGallerySessionWork(gallerySessionId)) {
						return Promise.resolve(driveList.driveItems[driveItemId] || {});
					}

					dispatch(fetchErrorDriveItemMeta(driveItemId));
					dispatch(createNotification('error.fetch_content_error', { id: driveItemId }));
					return Promise.reject({});
				});
		}

		return Promise.resolve(driveItemMeta || {});
	};

const requestDriveItemMeta = (driveItemId: number): RequestDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.REQUEST_DRIVE_META
});

const receiveDriveItemMeta = (driveItemId: number): ReceiveDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.RECEIVE_DRIVE_META
});

const fetchErrorDriveItemMeta = (driveItemId: number): FetchErrorDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.FETCH_ERROR_DRIVE_META
});

const canDriveListBeFetched = (driveList: DriveItemFetchState, block?: number): boolean => {
	return block !== undefined && (!driveList.contentBlocksStatus[block] || driveList.contentBlocksStatus[block] === FetchStatus.NOT_FETCHED);
};

const fetchContentBlock = (driveFolderId: number, block: number = 1, forceFetch: boolean = false) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
		const { driveList } = getState();
		const driveFolder = driveList.driveItems[driveFolderId];
		const driveItemList = getDriveItemStatusFromMap(driveList.driveItemsFetchStatus, driveFolderId);

		if (driveFolder && (forceFetch || canDriveListBeFetched(driveItemList, block))) {
			const { resolutionsBlock } = driveFolder;

			// If the first block is not already loaded, we load it, because it contains the resolution
			if (block !== resolutionsBlock && canDriveListBeFetched(driveItemList, resolutionsBlock)) {
				dispatch(fetchContentBlock(driveFolderId, resolutionsBlock));
			}

			dispatch(requestContentBlock(driveFolderId, block));

			return ApiUtils.getContentBlock(driveFolderId, block)
				.then((json: Api.DriveItemContentBlock) => {
					if (json.resolutions) {
						// Only store default resolution if we have not stored it yet
						// Will cause problem if the first folder fetched has custom resolution
						if (driveList.resolutions.default) {
							delete json.resolutions.default;
						}
						dispatch(receiveResolutions(json.resolutions));
					}
					dispatch(receiveDriveItems(json.metas));

					// download thumbnails for newly imported files in mobile app
					if (UserAgentUtils.isMobileApp) {
						const { offlineManager: { thumbMap } } = getState();

						Object.keys(json.metas)
							.filter(id => (
								!Object.keys(thumbMap).includes(id) &&
								json.metas[id].type !== Api.DriveItemType.Directory
							))
							.forEach(id => {
								dispatch(downloadThumb(parseInt(id, 10), DownloadPriority.Normal, false));
							});
					}

					const fetchIconMetasPromises = Object.keys(json.metas)
						.map(id => dispatch(fetchItemIconMeta(json.metas[id], json.resolutions)));

					return Promise.all(fetchIconMetasPromises)
						.then(() => dispatch(receiveContentBlock(driveFolderId, json, block)));
				})
				.catch(error => {
					dispatch(fetchErrorContentBlock(driveFolderId, block));
					dispatch(createNotification('error.fetch_content_block_error', { id: driveFolderId }));
					return {};
				});
		}
		return Promise.resolve({});
	};

const fetchItemIconMeta = (driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup } | undefined = undefined) =>
	(dispatch: Dispatch<StoreState>): Promise<{} | Api.DriveItem> => {
		const iconId = DriveItemUtils.getIconId(driveItem);

		if (
			iconId > 0 &&
			iconId !== driveItem.id &&
			DriveItemUtils.needsResolutions(driveItem) &&
			!DriveItemUtils.hasIconMetadata(driveItem, resolutions)) {

			return dispatch(getOrFetchDriveItemMeta(iconId));
		}

		return Promise.resolve({});
	};

const requestContentBlock = (driveFolderId: number, block: number): RequestContentBlockAction => ({
	driveFolderId,
	type: DriveItemListActionKeys.REQUEST_CONTENT_BLOCK,
	block
});

const receiveContentBlock = (driveFolderId: number, json: Api.DriveItemContentBlock, block: number): ReceiveContentBlockAction => {
	const { metas } = json;
	let driveItems: Api.DriveItem[] = [];
	Object.keys(metas).forEach(key => driveItems.push(metas[key]));

	return ({
		driveFolderId,
		type: DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK,
		block
	});
};

const fetchErrorContentBlock = (driveFolderId: number, block: number): FetchErrorContentBlockAction => ({
	driveFolderId,
	type: DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK,
	block
});

const receiveTemplateItems = (templateData: Api.PublicationTemplatesData) =>
	async (dispatch: Dispatch<StoreState>): Promise<void> => {
		const {
			masterProposals: masterTemplates,
			customTemplates,
			resolutions
		} = templateData;

		if (masterTemplates.length > 0) {
			for (const driveItem of masterTemplates) {
				await dispatch(updateTemplateDriveItem(driveItem, true));
			}
		}

		if (customTemplates.length > 0) {
			for (const driveItem of customTemplates) {
				await dispatch(updateTemplateDriveItem(driveItem, true));
			}
		}

		dispatch(receiveDriveItems(Utils.itemsToById(masterTemplates)));
		dispatch(receiveDriveItems(Utils.itemsToById(customTemplates)));
		dispatch(receiveResolutions(resolutions));
	};

const updateTemplateDriveItem = (driveItem: DriveItemInfo, isTemplate: boolean = false) =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		try {
			const isDownloadableType = DriveItemUtils.isDownloadableItemType(driveItem);

			if (isDownloadableType) {
				const updatedDriveItem = {
					...driveItem,
					isTemplate: isTemplate,
				};
				dispatch(updateDriveItem(updatedDriveItem));
			}
		} catch (error) {
			console.error('Failed to update template drive item:', error);
		}
	};

const receiveDriveItems = (driveItems: { [k: number]: Api.DriveItem }, isInGallery: boolean = true) =>
	(dispatch: Dispatch<StoreState>): Promise<void> => {
		return new Promise((resolve) => {
			const resolvedIsInGallery = !UserAgentUtils.isMobileApp ? undefined : isInGallery;

			const action: ReceiveDriveItemsAction = {
				type: DriveItemListActionKeys.RECEIVE_DRIVE_ITEMS,
				driveItems,
				isInGallery: resolvedIsInGallery,
			};

			dispatch(action);
			resolve();
		});
	};

const deleteFromAsp = (clientId: number, driveItem: Api.DriveItem) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setLoadingStatus(true));
		const resourceIdToTrash = driveItem.linkId || driveItem.id;
		const { undeletableResources } = await ApiUtils.trashResources(clientId, [resourceIdToTrash]);
		const resourcesNotDeleted = Object.keys(undeletableResources);
		const isResourceTrashed = !resourcesNotDeleted.includes(resourceIdToTrash.toString());

		if (!isResourceTrashed) {
			dispatch(createNotification('error.asset_not_erasable'));
		} else {
			const itemToTrashIds = DriveItemUtils.getDriveItemChildren(driveItem.id);
			const { driveList: { driveItems } } = getState();
			itemToTrashIds
				.map(itemDeletedId => driveItems[itemDeletedId])
				.forEach((itemDeleted => dispatch(deleteItem(itemDeleted))));
			dispatch(removeDriveItems(itemToTrashIds));
			dispatch(removeFromSelection(itemToTrashIds));
			// is needed to update 'resources' property of parent folder as after resource deleting structure of blocks is changed
			const parentId = DriveItemUtils.getParentFolderId(driveItem);
			await dispatch(getOrFetchDriveItemMeta(parentId, true, true, true));
		}
	} catch (error) {
		dispatch(createNotification('error.cant_remove_asset'));
	} finally {
		dispatch(setLoadingStatus(false));
	}
};

const removeDriveItems = (driveItemIds: number[]) => ({
	type: DriveItemListActionKeys.REMOVE_DRIVE_ITEMS,
	driveItemIds,
});

const receiveResolutions = (resolutions: { [k: string]: Api.ResolutionGroup }): ReceiveResolutionsAction => ({
	type: DriveItemListActionKeys.RECEIVE_RESOLUTIONS,
	resolutions
});

const updateDriveItem = (item: Api.DriveItem): UpdateDriveItemAction => ({
	type: DriveItemListActionKeys.UPDATE_DRIVE_ITEM,
	item,
});

const openContextualMenu = (driveItemId: number): OpenContextualMenuAction => ({
	type: DriveItemListActionKeys.OPEN_CONTEXTUAL_MENU,
	driveItemId,
});

const closeContextualMenu = (): CloseContextualMenuAction => ({
	type: DriveItemListActionKeys.CLOSE_CONTEXTUAL_MENU,
});

const lockContextualMenu = (): LockContextualMenuAction => ({
	type: DriveItemListActionKeys.LOCK_CONTEXTUAL_MENU,
});

const unlockContextualMenu = (): UnlockContextualMenuAction => ({
	type: DriveItemListActionKeys.UNLOCK_CONTEXTUAL_MENU,
});

const updateDriveItemName = (assetId: number, newLabel: string) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {

		const { driveList: { driveItems } } = getState();
		const renamedResource = Utils.deepCopy(driveItems[assetId]);
		renamedResource.label = newLabel;
		dispatch(updateDriveItem(renamedResource));
	};

const updateListDisplayType = (displayType: ListDisplayType) =>
	(dispatch: Dispatch<StoreState>): void => {
		IndexedDBManager.setDisplayType(displayType).then();

		dispatch({
			type: DriveItemListActionKeys.UPDATE_LIST_DISPLAY_TYPE,
			displayType
		});

		StatsUtils.trackDisplayType(displayType);
	};

const setReadStatus = (driveItemId: number) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { driveList, readStatus } = getState();
	const isUnread = DriveItemUtils.isUnread(driveList, readStatus, driveItemId);

	if (isUnread) {
		dispatch(updateLastVersionRead(driveItemId));
	}
};


interface PublicationOptions {
	page?: number;
	hideWelcomePage?: boolean;
	customSearchParams?: CustomSearchParams;
}

interface CustomSearchParams {
	[key: string]: string;
}

const viewableInGallery = [
	Api.DriveItemType.Directory,
	Api.DriveItemType.Image,
	Api.DriveItemType.Movie,
	Api.DriveItemType.Sound,
	Api.DriveItemType.Component,
];

const viewDriveItem = (driveItem: Api.DriveItem, origin: DriveItemOrigin, publicationOptions?: PublicationOptions, forceSameTab?: boolean) =>
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {

		if (!driveItem) {
			dispatch(createNotification('error.open_fail'));
			return;
		}

		if (driveItem.endDate && Utils.parseDate(driveItem.endDate) < new Date()) {
			dispatch(createNotification('error.content_expired', { id: driveItem.id }));
			return;
		}

		const { galleryInfos, routerParamsHistory, downloadManager, driveList, offlineManager } = getState();
		const galleryConfig = galleryInfos.config;
		const downloadMode = downloadManager.items[driveItem.id] ? downloadManager.items[driveItem.id].mode : undefined;
		const isAndroidApp = UserAgentUtils.isAndroidApp();
		const isWindowsApp = UserAgentUtils.isWindowsApp();
		const windowTarget = forceSameTab ? '_self' : '_blank';
		const isOnline = UserAgentUtils.isOnline();

		// Check if item is downloaded via downloadManager OR as a required item
		const isDownloadedInManager = DownloadManagerUtils.isItemDownloaded(downloadManager, driveItem.id);
		const requiredItemStatus = offlineManager.requiredItems[driveItem.id];
		const isDownloadedAsRequired = requiredItemStatus && requiredItemStatus.status === DownloadStatus.DOWNLOADED;
		const isDownloaded = isDownloadedInManager || isDownloadedAsRequired;

		if (driveItem.type !== Api.DriveItemType.Directory) {
			dispatch(setReadStatus(driveItem.id));
		}

		if (
			!isDownloaded &&
			![Api.DriveItemType.Directory, Api.DriveItemType.ExternalLink].includes(driveItem.type)
		) {
			if (isOnline) {
				await dispatch(checkActiveSession());
			} else {
				dispatch(createNotification('info.offline_download_single_asset'));
				return;
			}
		}

		StatsUtils.trackView(driveItem);
		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
				window.location.hash = `#${RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, driveItem.id.toString())}`;
				break;
			case Api.DriveItemType.Image:
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Sound:
				goToTheaterPage(driveItem, routerParamsHistory, galleryConfig, driveList, origin);
				break;
			case Api.DriveItemType.Publication:
				const {
					page = undefined,
					hideWelcomePage = undefined,
					customSearchParams = {},
				} = publicationOptions || {};
				let url = UrlUtils.getPublicationUrl(galleryConfig, driveItem, page, UserAgentUtils.isMobileApp, false, hideWelcomePage, customSearchParams);

				if (!UserAgentUtils.isMobileApp) {
					window.open(url, windowTarget);
					break;
				}

				// Use the local copy only when the zip and all dependencies are ready.
				const isPublicationOfflineReady = DownloadManagerUtils.isItemDownloaded(downloadManager, driveItem.id)
					&& !DownloadManagerUtils.isItemDownloading(downloadManager, driveItem.id);

				const viewPublicationStartMs = startTimedPhase(OfflineDownloadTimedPhase.ViewPublication, driveItem.id);
				let usedLocalUrl = false;

				logOfflineDownload(OfflineDownloadDebugAction.Timing, OfflineDownloadPhase.ViewPublicationState, driveItem.id, {
					offlineReady: isPublicationOfflineReady,
					confirmed: DownloadManagerUtils.isDownloadConfirmed(downloadManager, driveItem.id),
					downloading: DownloadManagerUtils.isItemDownloading(downloadManager, driveItem.id),
				});

				if (isPublicationOfflineReady) {
					// downloadConfirmed was set after native unzip + existFile at download time;
					// repairUnconfirmedDownloads handles legacy items on init.
					const downloadFolderPath = Utils.getPersistentDownloadsFolder();
					const publicationPath = `${driveItem.id}/index.html`;
					const publicationUrlParams = UrlUtils.getPublicationUrlParams(page, UserAgentUtils.isMobileApp, hideWelcomePage, customSearchParams);
					const localUrl = `${downloadFolderPath}/${publicationPath}${publicationUrlParams}`;
					url = Utils.getUrlForSecuredResource(localUrl);
					usedLocalUrl = true;

					logOfflineDownload(OfflineDownloadDebugAction.Timing, OfflineDownloadPhase.PublicationUseLocalUrl, driveItem.id, {
						skippedDiskCheck: true,
					});
				}

				logOfflineDownload(OfflineDownloadDebugAction.Timing, OfflineDownloadPhase.OpenFrameInvoke, driveItem.id, {
					isLocal: usedLocalUrl,
				});
				const openFrameStartMs = startTimedPhase(OfflineDownloadTimedPhase.OpenFrameNative, driveItem.id);
				const response = await NativeApi.openFrame(url);
				// openFrame promise resolves when the native reader closes, not when it first appears.
				endTimedPhase(OfflineDownloadTimedPhase.OpenFrameNative, openFrameStartMs, driveItem.id, {
					note: 'resolvesOnClose',
				});
				endTimedPhase(OfflineDownloadTimedPhase.ViewPublication, viewPublicationStartMs, driveItem.id);
				dispatch(openPublicationCallback(driveItem.id, response));
				break;
			case Api.DriveItemType.File:
				const fileName = DriveItemUtils.getDownloadedFileName(driveItem);
				const isItemPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);
				const isItemDownloaded = isItemPersistentlyDownloaded || downloadMode === DownloadMode.Dependency;

				if ((isAndroidApp || isWindowsApp) && !downloadMode) {
					dispatch(requestMobileAppDownload(driveItem, DownloadMode.Temp))
						.then(() => dispatch(openFile(fileName, tempFolder, driveItem)));
				} else {
					if (!isItemDownloaded && downloadMode !== DownloadMode.Temp) {
						const url = UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
						window.open(url, windowTarget);
					} else {
						const sourceFolder = downloadMode === DownloadMode.Temp
							? tempFolder
							: downloadsFolder;
						dispatch(openFile(fileName, sourceFolder, driveItem));
					}
				}
				break;
			case Api.DriveItemType.ExternalLink:
				window.open(driveItem.url, '_blank');
				break;
			case Api.DriveItemType.Component:
				window.location.hash = `#${RoutesUtils.getPathStaticPage(driveItem.id.toString())}`;
				break;
			default:
				dispatch(createNotification('error.open_fail'));
				// Types to ignore Template, V2Gateway, Link, AppContainer, Gallery
				break;
		}
	};

export const runCallbackAction = (action?: CallbackAction, currentAssetId?: number, currentPage?: number) =>
	async (dispatch: Dispatch<StoreState>): Promise<void> => {
		if (!action) {
			return;
		}

		switch (action.type) {
			case ActionType.NONE:
				return;
			case ActionType.VIEW_DRIVE_ITEM:
				const {
					assetId,
					page,
				} = action;
				const publicationOption: PublicationOptions = {
					page,
					hideWelcomePage: !!page
				};
				const driveItemToView: Api.DriveItem = await dispatch(getOrFetchDriveItemMeta(assetId, false, true)) as Api.DriveItem;
				dispatch(viewDriveItem(driveItemToView as Api.DriveItem, DriveItemOrigin.Menu, publicationOption));
				return;
			case ActionType.SHARE:
				if (!currentAssetId) {
					return;
				}
				const driveItemToShare: Api.DriveItem = await dispatch(getOrFetchDriveItemMeta(currentAssetId, true, true)) as Api.DriveItem;
				// TODO: It seems isPublicationShare can't be true anymore, we should remove all code related to it
				dispatch(share(
					[driveItemToShare],
					undefined,
					undefined,
					{
						type: ActionType.VIEW_DRIVE_ITEM,
						assetId: currentAssetId,
						page: currentPage,
					}
				));
				return;
		}
	};

const openPublicationCallback = (publicationId: number, response?: OpenFrameReturn) =>
	(dispatch: Dispatch<StoreState>): void => {
		if (response) {
			const { action, currentPage } = response;
			dispatch(runCallbackAction(action, publicationId, currentPage));
			dispatch(setLastPublication(publicationId, currentPage));
		}
	};

const openFile = (fileName: string, sourceFolder: string, driveItem: Api.DriveItem) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const { id, label, originalFile } = driveItem;
		const sourceUrl = `${sourceFolder}/${fileName}`;
		const specialCharsRegex = /!|"|#|\$|%|&|'|\*|\+|,|\/|:|;|<|=|>|\?|@|\[|\\|\]|\^|`|{|\||}|~|\s+/g; // only allow . - _ () in file name
		let correctedLabel = label.replace(specialCharsRegex, '_');
		const originalFileExtension = Utils.getFileExtension(originalFile);
		const currentFileExtension = Utils.getFileExtension(correctedLabel);

		if (originalFileExtension !== currentFileExtension) {
			correctedLabel += `.${originalFileExtension}`;
		}

		const isOpenedFilesFolderExist = await NativeApi.existDirectory(openedFilesFolder);

		if (!isOpenedFilesFolderExist) {
			await NativeApi.createDirectory(openedFilesFolder);
		}

		// each opened file is stored in folder with its id to allow opening different files with same name
		const fileFolder = `${openedFilesFolder}/${id}`;
		const isFileFolderExist = await NativeApi.existDirectory(fileFolder);

		if (!isFileFolderExist) {
			await NativeApi.createDirectory(fileFolder);
		}

		const destUrl = `${fileFolder}/${correctedLabel}`;

		// this checking is needed to check if file was renamed (destUrl is different)
		let isNewFileExist = await NativeApi.existFile(destUrl);

		if (!isNewFileExist) {
			await NativeApi.copyFile(sourceUrl, destUrl);
		}

		// this checking is needed to ensure that file was copied
		isNewFileExist = await NativeApi.existFile(destUrl);

		if (isNewFileExist) {
			const openedFilesFolderPath = UserAgentUtils.isWindowsApp()
				? Utils.getRelativeOpenedFilesFolder()
				: Utils.getPersistentOpenedFilesFolder();
			const newFileUrl = `${openedFilesFolderPath}/${id}/${correctedLabel}`;

			if (UserAgentUtils.isWindowsApp()) {
				NativeApi.openFileNatively(newFileUrl);
			} else {
				window.open(newFileUrl);
			}
		} else {
			dispatch(createNotification('error.open_fail'));
		}
	} catch (error) {
		dispatch(createNotification('error.open_fail'));
	}
};

const goToTheaterPage = async (
	driveItem: Api.DriveItem,
	routerParamsHistory: RouterParamsState[],
	galleryConfig: Api.Config,
	driveList: DriveListState,
	source: DriveItemOrigin
): Promise<void> => {
	const currentRoute = routerParamsHistory[routerParamsHistory.length - 1];
	const { params } = currentRoute;
	let origin;
	let originParam;

	if (source === DriveItemOrigin.List) {
		origin = (params.origin as DriveItemListOrigin) || DriveItemListOrigin.Folder;
		switch (origin) {
			case DriveItemListOrigin.Folder:
				originParam = params.originParam || await resolveParentId(driveItem.id, driveList);

				if (!originParam) {
					origin = DriveItemOrigin.Menu;
				}
				break;
			case DriveItemListOrigin.Search:
			case DriveItemListOrigin.Assortment:
				originParam = params.originParam;
				break;
		}
	} else if (source === DriveItemOrigin.Details) {
		origin = params.origin as DriveItemListOrigin || DriveItemListOrigin.Folder;
		originParam = params.folderId || await resolveParentId(driveItem.id, driveList);
	} else {
		origin = DriveItemOrigin.Menu;
	}

	window.location.hash = `#${RoutesUtils.getPathTheater(origin, originParam, driveItem.id)}`;
};

const resolveParentId = async (driveItemId: number, driveList: DriveListState): Promise<number | void> => {
	const { driveItems } = driveList;
	const driveItemsKeys = Object.keys(driveItems);

	for (let i = 0; i < driveItemsKeys.length; i++) {
		const folderId = parseInt(driveItemsKeys[i], 10);
		const folderMeta = driveItems[folderId];

		if (folderMeta && folderMeta.resources) {
			for (let j = 0; j < folderMeta.resources.length; j++) {
				const resource = folderMeta.resources[j];

				if (resource.id === driveItemId) {
					return folderId;
				}
			}
		}
	}

	try {
		const driveItem = await ApiUtils.getGalleryAsset(driveItemId);
		return DriveItemUtils.getParentFolderId(driveItem);
	} catch (error) {
		// If API call fails like in gallery master, return nothing.
		return;
	}
};

const setAppDisplayType = () => async (dispatch: Dispatch<StoreState>) => {
	const storedDisplayType = await IndexedDBManager.getDisplayType();
	const appDisplayType = storedDisplayType || ListDisplayType.Default;
	dispatch(updateListDisplayType(appDisplayType));
};

const setBreadcrumbs = (item: FolderState): SetBreadcrumbsAction => ({
	type: DriveItemListActionKeys.SET_BREADCRUMBS,
	item,
});

const resetBreadcrumbs = (): ResetBreadcrumbsAction => ({
	type: DriveItemListActionKeys.RESET_BREADCRUMBS,
});

const toggleEditMode = (): ToggleEditModeAction => ({
	type: DriveItemListActionKeys.TOGGLE_EDIT_MODE,
});

const getResourceId = (id: number): number => {
	const { driveList: { driveItems } } = store.getState();
	const { linkId } = driveItems[id];
	// if resource is linked we have to use linkId
	return linkId || id;
};

const moveDriveItem = (folderId: number, from: number, to: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		galleryInfos: { config, config: { clientId, applicationGallery } },
		routerParamsHistory,
		driveList,
		driveList: { driveItems },
	} = getState();

	const currentFolderId = Utils.getCurrentFolderId(applicationGallery, routerParamsHistory);
	const privateDirId = Utils.getPrivateDirId(driveList, config);
	const sourcesDirId = Utils.getSourcesDirId(driveList, config);
	const sharedDirId = Utils.getSharedDirId(driveList, config);
	const { resources } = driveItems[folderId];
	const isAppRootFolder = folderId === applicationGallery;
	// privateDir should be excluded for correct reordering (as it's not displayed in app root folder)
	const filteredResources = isAppRootFolder
		? resources!.filter(({ id }) => id !== privateDirId)
		: resources;
	const isPrivateDir = currentFolderId === privateDirId;
	const sourcesDirIndex = isPrivateDir
		? filteredResources!.findIndex(({ id }) => id === sourcesDirId)
		: undefined;
	const sharedDirIndex = isPrivateDir
		? filteredResources!.findIndex(({ id }) => id === sharedDirId)
		: undefined;

	let correctedFrom = from;
	let correctedTo = to;

	[sourcesDirIndex, sharedDirIndex].forEach((dirIndex) => {
		if (dirIndex !== undefined) {
			if (dirIndex <= from) {
				correctedFrom = correctedFrom + 1;
			}
			if (dirIndex <= to) {
				correctedTo = correctedTo + 1;
			}
		}
	});

	const isMoveToTop = correctedTo - correctedFrom < 0;
	const { id } = filteredResources![correctedFrom];
	const resourceId = getResourceId(id);
	const newIndex = isMoveToTop ? correctedTo : correctedTo - 1;
	const { position } = filteredResources![newIndex];
	const newPosition = isMoveToTop ? position as number + 1 : position as number;

	try {
		dispatch({
			type: DriveItemListActionKeys.MOVE_DRIVE_ITEM,
			folderId,
			from: correctedFrom,
			to: correctedTo,
			filteredResources,
			isAppRootFolder,
			privateDirId,
		});

		await ApiUtils.moveResourcesOrder(clientId, folderId, [resourceId], newPosition);
	} catch (error) {
		dispatch(createNotification('error.cant_move_asset'));
		dispatch(resetDriveOrder(folderId, resources as Api.DriveItemResource[]));
	}
};

const moveDriveItemInFolder = (folderId: number, id: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		galleryInfos: { config: { clientId, applicationGallery } },
		routerParamsHistory,
		driveList: { driveItems },
	} = getState();
	const currentFolderId = Utils.getCurrentFolderId(applicationGallery, routerParamsHistory);

	if (!currentFolderId) {
		return;
	}

	const { resources } = driveItems[currentFolderId];
	const resourceId = getResourceId(id);

	try {
		dispatch({
			type: DriveItemListActionKeys.MOVE_DRIVE_ITEM_IN_FOLDER,
			folderId: currentFolderId,
			id,
		});
		dispatch(setLoadingStatus(true));

		await ApiUtils.moveResources(clientId, folderId, [resourceId]);
		await Promise.all([
			dispatch(getOrFetchDriveItemMeta(currentFolderId, true, true, true)),
			dispatch(getOrFetchDriveItemMeta(folderId, true, true, true)),
			dispatch(getOrFetchDriveItemMeta(resourceId, true, true, true)),
		]);
	} catch (error) {
		dispatch(createNotification('error.cant_move_asset'));
		dispatch(resetDriveOrder(currentFolderId, resources as Api.DriveItemResource[]));
	} finally {
		dispatch(setLoadingStatus(false));
	}
};

const resetDriveOrder = (folderId: number, resources: Api.DriveItemResource[]): ResetDriveOrderAction => ({
	type: DriveItemListActionKeys.RESET_DRIVE_ORDER,
	folderId,
	resources,
});

const setLoadingStatus = (isLoading: boolean): SetLoadingStatusAction => ({
	type: DriveItemListActionKeys.SET_LOADING_STATUS,
	isLoading,
});

const setFolderDisplayType = (folderId: number | undefined, displayType: ListDisplayType) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { routerParamsHistory } = getState();
	const folderIdOrOrigin = folderId || RouteUtils.getCurrentOrigin(routerParamsHistory);

	dispatch({
		type: DriveItemListActionKeys.SET_FOLDER_DISPLAY_TYPE,
		folderId: folderIdOrOrigin,
		displayType,
	});

	IndexedDBManager.setFolderDisplayType(folderIdOrOrigin, displayType).then();
};

const setFoldersDisplayType = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const foldersDisplayType = await IndexedDBManager.getFoldersDisplayType() || {};
	dispatch({
		type: DriveItemListActionKeys.SET_FOLDERS_DISPLAY_TYPE,
		data: foldersDisplayType,
	});
};

const resetFoldersDisplayType = () => (dispatch: Dispatch<StoreState>): void => {
	dispatch({
		type: DriveItemListActionKeys.RESET_FOLDERS_DISPLAY_TYPE,
	});

	IndexedDBManager.resetFoldersDisplayType().then();
};

const getCustomTemplatesIds = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const {
			driveList: { customTemplates: { isLoaded } },
		} = getState();

		if (!UserAgentUtils.isOnline() || isLoaded) {
			return;
		}

		dispatch(setLoadingStatus(true));
		const { customTemplates } = await ApiUtils.getPublicationTemplates();
		const ids = customTemplates.map(({ id }) => id);
		dispatch(setCustomTemplatesIds(ids));
	} catch (error) {
		dispatch(createNotification('error.fetch_publication_templates_error'));
	} finally {
		dispatch(setLoadingStatus(false));
	}
};

const setCustomTemplatesIds = (ids: number[]): SetCustomTemplatesIdsAction => ({
	type: DriveItemListActionKeys.SET_CUSTOM_TEMPLATES_IDS,
	ids,
});

const addCustomTemplate = (id: number): AddCustomTemplateAction => ({
	type: DriveItemListActionKeys.ADD_CUSTOM_TEMPLATE,
	id,
});

const removeCustomTemplate = (id: number): RemoveCustomTemplateAction => ({
	type: DriveItemListActionKeys.REMOVE_CUSTOM_TEMPLATE,
	id,
});

const updateLastFolderTreeUpdate = (): SetLastFolderTreeUpdateAction => {
	const currentTimestamp = Date.now();
	return {
		type: DriveItemListActionKeys.SET_LAST_FOLDER_TREE_UPDATE,
		lastFolderTreeUpdate: currentTimestamp,
	};
};

const startFolderTreeUpdate = (): StartFolderTreeUpdateAction => ({
	type: DriveItemListActionKeys.START_FOLDER_TREE_UPDATE,
});

const endFolderTreeUpdate = (): EndFolderTreeUpdateAction => ({
	type: DriveItemListActionKeys.END_FOLDER_TREE_UPDATE,
});

const archiveProposal = (clientId: number, driveItem: Api.DriveItem) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setLoadingStatus(true));
		const isArchived = await ApiUtils.archiveResources(clientId, [driveItem.id]);

		if (isArchived) {
			dispatch(removeDriveItems([driveItem.id]));

			const {
				driveList,
				galleryInfos: { config },
			} = getState();
			const archiveDirId = Utils.getArchiveDirId(driveList, config) as number;
			const parentId = DriveItemUtils.getParentFolderId(driveItem);

			await Promise.all([
				// is needed to update 'resources' property of parent folder as after resource archiving structure of blocks is changed
				dispatch(getOrFetchDriveItemMeta(parentId, true, true, true)),
				dispatch(getOrFetchDriveItemMeta(archiveDirId, true, true, true)),
			]);
		} else {
			dispatch(createNotification('error.proposal_not_archived'));
		}
	} catch (error) {
		dispatch(createNotification('error.cant_archive_proposal'));
	} finally {
		dispatch(setLoadingStatus(false));
	}
};

const getAspDriveItem = (globalId: number) => async (dispatch: Dispatch<StoreState>): Promise<Api.AspDriveResourceItem | undefined> => {
	let driveItem: Api.AspDriveResourceItem | undefined;

	try {
		driveItem = await ApiUtils.getResource(globalId);
	} catch (error) {
		dispatch(createNotification('error.cant_get_resource'));
	}

	return driveItem;
};

const yieldToUiThread = (): Promise<void> => new Promise((resolve) => {
	setTimeout(resolve, 0);
});

const refreshCurrentFolderAfterTreeUpdate = () => (
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		const {
			galleryInfos: { config },
			routerParamsHistory,
			driveList,
		} = getState();
		const currentFolderId = Utils.getCurrentFolderId(config.applicationGallery, routerParamsHistory);

		if (!currentFolderId) {
			return;
		}

		const folder = driveList.driveItems[currentFolderId];
		if (!folder || folder.type !== Api.DriveItemType.Directory) {
			return;
		}

		try {
			const folderMeta = await dispatch(
				getOrFetchDriveItemMeta(currentFolderId, true, true, true)
			) as Api.DriveFolderInfo;

			if (folderMeta && folderMeta.resources && folderMeta.resources.length > 0) {
				const resolutionsBlock = folderMeta.resolutionsBlock || 1;
				const firstResource = folderMeta.resources[0];
				const firstBlock = firstResource && firstResource.block
					? firstResource.block
					: resolutionsBlock;
				await dispatch(fetchContentBlock(currentFolderId, firstBlock, true));
			}
		} catch (error) {
			// Non-fatal: tree was updated; refreshing the open folder list is best-effort
		}
	});

const updateFolderTree = (isUserAction: boolean) => (
	async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		const {
			driveList: { folderTreeUpdate: { isUpdating } },
			offlineManager: { isPreparingContentStructure },
		} = getState();

		if (!UserAgentUtils.isOnline()) {
			if (isUserAction) {
				dispatch(createNotification('info.offline_update_folder_tree'));
			}
			return;
		}

		if (isUpdating || isPreparingContentStructure) {
			return;
		}

		dispatch(startFolderTreeUpdate());
		if (isUserAction) {
			dispatch(startContentStructurePreparation());
		}

		try {
			await yieldToUiThread();
			dispatch(resetFetchAllStatus());
			await dispatch(getAllDriveItems());
			if (UserAgentUtils.isMobileApp) {
				await dispatch(syncOfflineDependenciesAfterContentRefresh({
					resumeInterrupted: false,
					autoUpdateImportant: true,
				}));
			}
			await dispatch(refreshCurrentFolderAfterTreeUpdate());
		} catch (error) {
			if (isUserAction) {
				dispatch(createNotification('error.fetch_folder_tree'));
			}
		} finally {
			dispatch(endFolderTreeUpdate());
			if (isUserAction) {
				dispatch(endContentStructurePreparation());
			}
		}
	});

export {
	DriveItemListActionKeys,
	DriveItemListAction,
	DriveItemOrigin,
	fetchDriveItem,
	fetchContentBlock,
	resetFetchAllStatus,
	getAllDriveItems,
	getOrFetchDriveItemMeta,
	receiveTemplateItems,
	receiveDriveItems,
	receiveResolutions,
	receiveDriveItemMeta,
	updateListDisplayType,
	updateDriveItem,
	openContextualMenu,
	closeContextualMenu,
	lockContextualMenu,
	unlockContextualMenu,
	PublicationOptions,
	CustomSearchParams,
	viewableInGallery,
	viewDriveItem,
	resolveParentId,
	setReadStatus,
	setBreadcrumbs,
	resetBreadcrumbs,
	updateDriveItemName,
	toggleEditMode,
	removeDriveItems,
	moveDriveItem,
	moveDriveItemInFolder,
	resetDriveOrder,
	setLoadingStatus,
	setFolderDisplayType,
	setFoldersDisplayType,
	resetFoldersDisplayType,
	getCustomTemplatesIds,
	setCustomTemplatesIds,
	addCustomTemplate,
	removeCustomTemplate,
	updateLastFolderTreeUpdate,
	deleteFromAsp,
	archiveProposal,
	getAspDriveItem,
	updateFolderTree,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemList/actions.ts